// Nateva's Health tab: the shapes the database answers with (nateva-platform supabase/migrations/0096 to 0098,
// public.health_overview) and the small plain-words helpers the page uses. Pure functions, no React and no
// network, so they can be tested on their own. The page shows; it changes nothing.

export type Light = 'green' | 'amber' | 'red'

export interface Reason { level: 'amber' | 'red'; text: string }

export interface TableRow { table: string; rows: number; sample_rows: number | null; real_rows: number | null; last_change_at: string | null }
export interface StaleRow { table: string; rows: number; last_change_at: string | null; days: number }
export interface AppData {
  app: string; label: string; tables: number; rows: number; sample_rows: number; real_rows: number; empty_tables: number
  last_change_at: string | null; stale_tables: StaleRow[]; table_list: TableRow[]
}
export interface DataHealth {
  checked_at: string; light: Light; headline: string; reasons: Reason[]; sample_mode: boolean; guest_open: boolean
  stale_days: number; apps: AppData[]
  checks: {
    carbon: { passed: number; failed: number; needs_person: number }
    carbon_drafts: { failed: number; needs_person: number }
    yield_blocks: { blocks: number; gap_too_big: number; too_few_plots: number; plots_too_old: number }
    missing_settings: string[]
  }
  model_spend_today: { app: string; spent_usd: number; cap_usd: number | null }[]
  migrations: { database_record_last: string | null; database_record_count: number; recorded_here: { file: string; applied_at: string; applied_by: string | null }[] }
}

export interface Flagged { name: string; severity: 'critical' | 'high' | 'moderate' | 'low'; title: string; url: string | null; direct: boolean; fix: string }
export interface RepoHealth {
  repo: string; is_production: boolean; is_archived: boolean; default_branch: string | null
  last_commit_at: string | null; last_commit_sha: string | null; last_commit_by: string | null
  has_ci: boolean; ci_workflow: string | null; ci_conclusion: 'success' | 'failure' | 'cancelled' | 'in_progress' | 'none' | 'unknown' | null
  ci_run_at: string | null; ci_run_url: string | null
  dependabot: 'on' | 'off' | 'unknown'
  alerts: { critical: number | null; high: number | null; medium: number | null; low: number | null }
  code_scanning: 'on' | 'off' | 'unknown'; code_scanning_open: number | null
  secret_scanning: 'on' | 'off' | 'unknown'
  branch_protection: 'on' | 'off' | 'not_on_plan' | 'unknown'
  deps_count: number | null; dev_deps_count: number | null
  audit: { source: string | null; critical: number | null; high: number | null; moderate: number | null; low: number | null }
  flagged: Flagged[]; main_libraries: { name: string; version: string }[]; recommendations: string[]; error: string | null
  owner: string | null; owner_name: string | null; owner_note: string | null; owner_set_at: string | null
  light: Light
}
export interface SoftwareHealth {
  checked_at: string | null; age_days?: number; light: Light; headline: string; reasons: Reason[]
  totals: Record<string, number>; repos: RepoHealth[]; recommendations: { text: string; repos: string[] }[]
  history: { taken_at: string; repos: number; critical_and_high: number }[]
  newest_migration_file?: string | null; migration_files?: number | null
}

export interface PersonLine { name: string; email: string; days_since_added?: number; signed_in?: boolean; last_sign_in_at?: string | null }
export interface AccessHealth {
  checked_at: string; light: Light; headline: string; reasons: Reason[]
  people: { total: number; signed_in: number; never_signed_in: PersonLine[]; removed_or_inactive: number; no_apps: PersonLine[]; with_end_date: { name: string; email: string; expires_at: string; expired: boolean }[]; list: (PersonLine & { is_admin: boolean; has_apps: boolean; expired: boolean })[] }
  admins: { count: number; signed_in: number; list: PersonLine[] }
  keys: { in_force: number; in_use: number; revoked: number; expired: number; unused_days: number; never_used: { name: string; email: string; prefix: string; days_old: number }[]; list: { name: string; email: string; prefix: string; created_at: string; last_used_at: string | null; expires_at: string; state: string }[] }
  guest: { sample_mode: boolean; guests_can_open_sample_data: boolean; words: string }
}

export interface HealthOverview { checked_at: string; light: Light; data: DataHealth; software: SoftwareHealth; access: AccessHealth }

// ---- words ------------------------------------------------------------------------------------------------------

export const LIGHT_WORD: Record<Light, string> = { green: 'All good', amber: 'Needs a look', red: 'Fix now' }

/** "3 days ago", "just now", "yesterday": how long since a time, in plain words. */
export function ago(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'never'
  const mins = Math.round((now.getTime() - new Date(iso).getTime()) / 60_000)
  if (mins < 2) return 'just now'
  if (mins < 60) return `${mins} minutes ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`
  const days = Math.round(hrs / 24)
  if (days === 1) return 'yesterday'
  if (days < 45) return `${days} days ago`
  return new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** A date and time in NZ words, for "last checked". */
export function nzWhen(iso: string | null | undefined): string {
  if (!iso) return 'not yet'
  return new Intl.DateTimeFormat('en-NZ', { timeZone: 'Pacific/Auckland', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(iso))
}

export const n = (v: number | null | undefined): string => (v === null || v === undefined ? 'n/a' : v.toLocaleString('en-NZ'))

/** The result of the latest build on main, in words. */
export function buildWord(r: Pick<RepoHealth, 'ci_conclusion' | 'has_ci'>): { tone: 'ok' | 'danger' | 'warn' | 'info'; text: string } {
  if (!r.has_ci) return { tone: 'warn', text: 'No automatic check' }
  switch (r.ci_conclusion) {
    case 'success': return { tone: 'ok', text: 'Passing' }
    case 'failure': return { tone: 'danger', text: 'Failing' }
    case 'in_progress': return { tone: 'info', text: 'Running' }
    case 'cancelled': return { tone: 'warn', text: 'Cancelled' }
    default: return { tone: 'info', text: 'Not known' }
  }
}

/** GitHub's own switches in words: on, off, or why they cannot be known. */
export function switchWord(v: 'on' | 'off' | 'unknown'): { tone: 'ok' | 'warn' | 'info'; text: string } {
  return v === 'on' ? { tone: 'ok', text: 'On' } : v === 'off' ? { tone: 'warn', text: 'Off' } : { tone: 'info', text: 'Cannot see' }
}

export function protectionWord(v: RepoHealth['branch_protection']): { tone: 'ok' | 'warn' | 'info'; text: string } {
  switch (v) {
    case 'on': return { tone: 'ok', text: 'Locked' }
    case 'off': return { tone: 'warn', text: 'Not locked' }
    case 'not_on_plan': return { tone: 'warn', text: 'Not locked (free plan)' }
    default: return { tone: 'info', text: 'Cannot see' }
  }
}

/** Open weakness counts for a repo by size, from GitHub alerts and the weak-library check together. */
export function weakness(r: Pick<RepoHealth, 'alerts' | 'audit'>) {
  const sum = (a: number | null, b: number | null) => (a ?? 0) + (b ?? 0)
  const checked = r.audit.source !== null || r.alerts.critical !== null
  return {
    checked,
    critical: sum(r.alerts.critical, r.audit.critical),
    high: sum(r.alerts.high, r.audit.high),
    moderate: sum(r.alerts.medium, r.audit.moderate),
    low: sum(r.alerts.low, r.audit.low),
  }
}

export function weaknessWord(r: Pick<RepoHealth, 'alerts' | 'audit'>): { tone: 'ok' | 'danger' | 'warn' | 'info'; text: string } {
  const w = weakness(r)
  if (!w.checked) return { tone: 'info', text: 'Not checked' }
  const total = w.critical + w.high + w.moderate + w.low
  if (total === 0) return { tone: 'ok', text: 'None known' }
  const parts = [w.critical && `${w.critical} critical`, w.high && `${w.high} high`, w.moderate && `${w.moderate} moderate`, w.low && `${w.low} low`].filter(Boolean)
  return { tone: w.critical + w.high > 0 ? 'danger' : 'warn', text: parts.join(', ') }
}

/** The apps first, then the rest, as the page lists the repos. */
export function sortRepos(repos: RepoHealth[]): RepoHealth[] {
  const rank: Record<Light, number> = { red: 0, amber: 1, green: 2 }
  return [...repos].sort((a, b) => Number(b.is_production) - Number(a.is_production) || rank[a.light] - rank[b.light] || a.repo.localeCompare(b.repo))
}

/** True when the signed-in person may open the Health tab: a real admin who is not seeing it as someone else. */
export function canOpenHealth(me: { is_admin?: boolean; viewing_as?: string | null } | null | undefined): boolean {
  return Boolean(me?.is_admin && !me.viewing_as)
}
